import type { FC, ReactNode } from "react";
import { createContext, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { API } from "aws-amplify";
import { orderBy } from "lodash";
import { Utils } from "../utils/utils";
import { useSnackbars } from "../hooks/use-snackbars";
import { SNACKBAR_MESSAGE } from "../components/SnackbarsHandler";
import { ISignature } from "../../types/signature";
import { useLoadings } from "../hooks/use-loadings";

type Signatures = any[];

export enum SignatureLoading {
  fetchSignatures = "fetchSignatures",
  fetchSignatureById = "fetchSignatureById",
  fetchSignatureDetailsById = "fetchSignatureDetailsById",
  createSignature = "createSignature",
  updateSignature = "updateSignature",
  cancelSignature = "cancelSignature",
  sendReminder = "sendReminder",
  downloadFile = "downloadFile",
}

export interface SignaturesContextValue {
  signatures: any[];
  fetchedSignatures: any[];
  updateSignaturesArray: (newFilteredArray: any[]) => void;
  selectedSignature?: any;
  fetchSignatures: () => Promise<void>;
  fetchSignatureById: (signatureId: string | undefined) => Promise<void>;
  fetchSignatureDetailsById: (signatureId: string | undefined) => Promise<void>;
  createSignature: (signature: ISignature) => Promise<void>;
  updateSignature: (signature: ISignature) => Promise<void>;
  cancelSignature: (signature: ISignature) => Promise<void>;
  sendReminder: (signature: ISignature) => Promise<void>;
  downloadFile: (signature: ISignature) => Promise<void>;
  showPdfPreviewButton: boolean;
  setShowPdfPreviewButton: (val: boolean) => void;
}

interface SignaturesProviderProps {
  children?: ReactNode;
}

const initialSignatures: Signatures = [];

export const SignaturesContext = createContext<SignaturesContextValue>({
  signatures: initialSignatures,
  fetchedSignatures: initialSignatures,
  updateSignaturesArray: (newFilteredArray: any[]) => undefined,
  selectedSignature: undefined,
  fetchSignatures: () => Promise.resolve(),
  fetchSignatureById: (signatureId) => Promise.resolve(),
  fetchSignatureDetailsById: (signatureId) => Promise.resolve(),
  createSignature: (signature) => Promise.resolve(),
  updateSignature: (signature) => Promise.resolve(),
  cancelSignature: (signature) => Promise.resolve(),
  sendReminder: (signature) => Promise.resolve(),
  downloadFile: (signature) => Promise.resolve(),
  showPdfPreviewButton: false,
  setShowPdfPreviewButton: (val: boolean) => undefined,
});

export const SignaturesProvider: FC<SignaturesProviderProps> = (props) => {
  const { children } = props;
  const { loading, loadingFinished } = useLoadings();
  const navigate = useNavigate();
  const [signatures, setSignatures] = useState<Signatures>(initialSignatures);
  const [fetchedSignatures, setFetchedSignatures] = useState<Signatures>(initialSignatures);
  const [selectedSignature, setSelectedSignature] = useState<any>(undefined);
  const { createSnackbar } = useSnackbars();
  const [showPdfPreviewButton, setShowPdfPreviewButton] = useState<boolean>(false);

  const fetchSignatures = async () => {
    loading(SignatureLoading.fetchSignatures);
    API.get("API", "/signatures", {})
      .then((response) => {
        const orderedSignature = orderBy(response, "createdAt", "desc") || [];
        setSignatures(orderedSignature);
        setFetchedSignatures(orderedSignature);
      })
      .catch(() => {
        createSnackbar(SNACKBAR_MESSAGE.DANGER.FETCH_SIGNATURES);
      })
      .finally(() => loadingFinished(SignatureLoading.fetchSignatures));
  };

  const fetchSignatureById = async (signatureId: string | undefined) => {
    setSelectedSignature(undefined);
    if (signatureId && signatureId !== "new") {
      loading(SignatureLoading.fetchSignatureById);
      API.get("API", `/signatures/${signatureId}`, {})
        .then((response) => {
          // console.log("response:", response);
          setSelectedSignature(response);
        })
        .catch(() => {
          createSnackbar(SNACKBAR_MESSAGE.DANGER.FETCH_SIGNATURE);
        })
        .finally(() => loadingFinished(SignatureLoading.fetchSignatureById));
    }
  };

  const createSignature = async (signature: ISignature) => {
    signature.fileBase64 = undefined;
    signature.file = undefined;

    loading(SignatureLoading.createSignature);
    API.post("API", "/signatures", { body: signature })
      .then((response) => {
        createSnackbar(SNACKBAR_MESSAGE.SUCCESS.CREATE_SIGNATURE);
        setSelectedSignature(response.data);
        navigate(`/signatures/${response.id}`);
      })
      .catch(() => {
        createSnackbar(SNACKBAR_MESSAGE.DANGER.CREATE_SIGNATURE);
      })
      .finally(() => loadingFinished(SignatureLoading.createSignature));
  };

  const updateSignature = async (signature: ISignature) => {
    loading(SignatureLoading.updateSignature);
    API.post("API", `/signatures/${signature.id}`, { body: signature })
      .then((response) => {
        setSelectedSignature(response);
      })
      .catch(() => {
        createSnackbar(SNACKBAR_MESSAGE.DANGER.UPDATE_SIGNATURE);
      })
      .finally(() => loadingFinished(SignatureLoading.updateSignature));
  };

  const cancelSignature = async (signature: ISignature) => {
    loading(SignatureLoading.cancelSignature);
    API.del("API", `/signatures/${signature.id}`, {})
      .then((response) => {
        const newSignatures = [...signatures];
        const updatedSignature = newSignatures.find((s) => s.id === signature.id);
        if (updatedSignature) {
          updatedSignature.status = "CANCELLED";
        }
        setSignatures(newSignatures);
        createSnackbar(SNACKBAR_MESSAGE.SUCCESS.CANCEL_SIGNATURE);
      })
      .catch(() => {
        createSnackbar(SNACKBAR_MESSAGE.DANGER.CANCEL_SIGNATURE);
      })
      .finally(() => loadingFinished(SignatureLoading.cancelSignature));
  };

  const sendReminder = async (signature: ISignature) => {
    loading(SignatureLoading.sendReminder);
    API.post("API", `/signatures/${signature.id}/reminder`, {})
      .then((response) => {
        const newSignatures = [...signatures];
        const updatedSignature = newSignatures.find((s) => s.id === signature.id);
        if (updatedSignature) {
          updatedSignature.nextReminderAvailability = response.nextReminderAvailability;
        }
        setSignatures(newSignatures);
        createSnackbar(SNACKBAR_MESSAGE.SUCCESS.SEND_REMINDER);
      })
      .catch((err) => {
        console.log(err.data);
        createSnackbar(SNACKBAR_MESSAGE.DANGER.SEND_REMINDER);
      })
      .finally(() => loadingFinished(SignatureLoading.sendReminder));
  };

  const downloadFile = async (signature: ISignature) => {
    loading(SignatureLoading.downloadFile);
    API.get("API", `/signatures/${signature.id}/download`, {})
      .then((response) => {
        Utils.downloadFile(response.documentUrl, response.signatureTitle || response.documentName);
      })
      .catch(() => {
        createSnackbar(SNACKBAR_MESSAGE.DANGER.DOWNLOAD_FILE);
      })
      .finally(() => loadingFinished(SignatureLoading.downloadFile));
  };

  const fetchSignatureDetailsById = async (signatureId: string | undefined) => {
    setSelectedSignature(undefined);
    if (signatureId && signatureId !== "new") {
      loading(SignatureLoading.fetchSignatureDetailsById);
      API.get("API", `/signatures/${signatureId}/details`, {})
        .then((response) => {
          // console.log("response:", response);
          setSelectedSignature(response);
        })
        .catch(() => {
          createSnackbar(SNACKBAR_MESSAGE.DANGER.FETCH_SIGNATURE_DETAILS);
        })
        .finally(() => loadingFinished(SignatureLoading.fetchSignatureDetailsById));
    }
  };

  const updateSignaturesArray = (newArray: any[]) => {
    setSignatures(newArray);
  };

  return (
    <SignaturesContext.Provider
      value={{
        signatures,
        fetchedSignatures,
        updateSignaturesArray,
        selectedSignature,
        fetchSignatures,
        fetchSignatureById,
        fetchSignatureDetailsById,
        createSignature,
        updateSignature,
        cancelSignature,
        downloadFile,
        sendReminder,
        showPdfPreviewButton,
        setShowPdfPreviewButton,
      }}
    >
      {children}
    </SignaturesContext.Provider>
  );
};

SignaturesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const SignaturesConsumer = SignaturesContext.Consumer;
